// src/App.tsx
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "components/ui/toaster";           // Lovable Toaster
import { TooltipProvider } from "components/ui/tooltip";    // Lovable Tooltip
import { LandingPage } from "./pages/LandingPage";
import { LoginPage } from "./pages/LoginPage";
import { ArtifactsPage } from "./pages/ArtifactsPage";
import TranslationPage from "./pages/TranslationPage";
import { PricePage } from "./pages/PricePage";
import UploadPage from "./pages/UploadPage";
import FAQPage from "./pages/FAQPage";
import { LogoutPage } from "./pages/LogoutPage";
import LanguagePage from "./pages/LanguagePage";
import { Layout } from "./components/Layout";
import { WebSocketProvider } from "./providers/WebSocketProvider";
import { AboutPage } from "./pages/AboutPage";
import { RegisterPage } from "pages/RegisterPage";
import SponsorshipPage from "pages/SponsorshipPage";
import { SettingsPage } from "pages/SettingsPage";
import { StepByStepVoiceoverPage } from "./pages/StepByStepVoiceoverPage";

import ProjectsPage from "./pages/ProjectsPage";
import ProjectWorkspace from "./pages/ProjectWorkspace";
import DemoPage from "./pages/DemoPage";
import FilesPage from "./pages/FilesPage";

const queryClient = new QueryClient();

// keep your PrivateRoute as-is
const PrivateRoute = ({ children }: { children: JSX.Element }) => {
  const token = localStorage.getItem("token");
  return token ? children : <Navigate to="/landing" />;
};

function App() {
  return (
    // React Query provider
    <QueryClientProvider client={queryClient}>
      {/* Tooltip context */}
      <TooltipProvider>
          {/* Lovable Toasters for new UI notifications */}
          <Toaster />

          {/* Your existing WebSocket context */}
          <WebSocketProvider>
            <BrowserRouter>
              <Routes>
                {/* Public routes */}
                <Route path="/landing" element={<Layout><LandingPage /></Layout>} />
                
                <Route path="/login" element={<Layout><LoginPage /></Layout>} />
                <Route path="/register" element={<Layout><RegisterPage /></Layout>} />
                
                {/* FAQ and Pricing pages */}
                <Route path="/price" element={<Layout><PricePage /></Layout>} />
                <Route path="/faq" element={<Layout><FAQPage /></Layout>} />
                <Route path="/about" element={<Layout><AboutPage /></Layout>} />
                <Route path="/demo" element={<DemoPage />} />

                {/* Private routes */}
                <Route
                  path="/"
                  element={
                    <PrivateRoute>
                      <Layout>
                        <ProjectsPage />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/translation/:id?"
                  element={
                    <PrivateRoute>
                      <Layout>
                        <TranslationPage />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/tasks"
                  element={
                    <PrivateRoute>
                      <Layout>
                        <ProjectsPage />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route path="/files" element={<PrivateRoute><Layout><FilesPage /></Layout></PrivateRoute>} />
                <Route
                  path="/upload"
                  element={
                    <PrivateRoute>
                      <Layout>
                        <UploadPage />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/voices"
                  element={
                    <PrivateRoute>
                      <LanguagePage />
                    </PrivateRoute>
                  }
                />
                <Route path="/sponsorship" element={
                  <PrivateRoute>
                    <Layout>
                      <SponsorshipPage />
                    </Layout>
                  </PrivateRoute>
                } 
                />
                <Route
                  path="/settings"
                  element={
                    <PrivateRoute>
                      <SettingsPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/voiceover/step-by-step/:translation_id"
                  element={
                    <PrivateRoute>
                      <StepByStepVoiceoverPage />
                    </PrivateRoute>
                  }
                />
                <Route path="/projects/:projectId" element={<PrivateRoute><Layout><ProjectWorkspace /></Layout></PrivateRoute>} />
                <Route path="/projects/:projectId/source/:fileId" element={<PrivateRoute><Layout><ProjectWorkspace /></Layout></PrivateRoute>} />
                <Route path="/projects/:projectId/task/:id" element={<PrivateRoute><Layout><ProjectWorkspace /></Layout></PrivateRoute>} />
                <Route path="/projects/:projectId/audio/:audioId" element={<PrivateRoute><Layout><ProjectWorkspace /></Layout></PrivateRoute>} />
                <Route path="/projects/:projectId/voice/:translation_id" element={<PrivateRoute><Layout><ProjectWorkspace /></Layout></PrivateRoute>} />
                <Route path="/legacy-tasks" element={<PrivateRoute><Layout><ArtifactsPage /></Layout></PrivateRoute>} />
                <Route path="/logout" element={<LogoutPage />} />

                {/* Catch-all: redirect to landing */}
                <Route path="*" element={<Navigate to="/landing" replace />} />
              </Routes>
            </BrowserRouter>
          </WebSocketProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
